import styles from './styles.module.scss';
import { useEffect, useMemo, useState, useRef } from 'react';
import EventListing from '../EventListing/EventListing';
import { useSocket } from '../../../Context/socket';
import { useSession } from 'next-auth/react';
import { BarLoader, PulseLoader } from 'react-spinners';
import errorMsg from '../../../Functions/errorMsg';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faQrcode, faRotate, faTicket, faXmark } from '@fortawesome/free-solid-svg-icons';
import { QRCode } from 'react-qrcode-logo';

export default function Purchases({ setPendingRequest }) {
  const { data: session, status } = useSession();
  const { socket } = useSocket();
  const [initialLoading, setInitialLoading] = useState(null);
  const [myListings, setMyListings] = useState(null);

  useEffect(() => {
    if (initialLoading !== null || myListings !== null || status === 'loading' || !session) return;

    async function getMyTickets() {
      setInitialLoading(true);
      setPendingRequest(true);

      try {
        const result = await axios.post(`${window.location.origin}/api/eventHub/tickets/getTickets`, {
          userID: session.user.token.id,
        });

        if (result.status === 200) {
          const data = result.data;
          setMyListings(data.sort((a, b) => new Date(b.creationDate) - new Date(a.creationDate)));
        }
      } catch (error) {
        errorMsg(error?.response?.data?.status || error.toString());
      } finally {
        setPendingRequest(false);
      }
    }

    getMyTickets();
  }, [session, status, initialLoading, myListings]);

  function isLoading() {
    return status === 'loading' || initialLoading !== true || myListings === null;
  }

  function refreshListings() {
    setMyListings(null);
    setInitialLoading(null);
  }

  const [viewEvent, setViewEvent] = useState(null);
  const [viewQR, setViewQR] = useState(null);

  return (
    <section id={styles.wrapper}>
      {viewEvent !== null && <EventListing listingID={viewEvent} closeListing={setViewEvent} />}
      {viewQR !== null && <QRCodeDetails closeDetails={() => setViewQR(null)} data={viewQR} />}

      {isLoading() && (
        <section id={styles.loading}>
          <PulseLoader size={30} color="#154734" />
        </section>
      )}

      {!isLoading() && myListings.length === 0 && (
        <div id={styles.noListings}>
          <h1>No ticket purchases found</h1>
        </div>
      )}

      {!isLoading() && myListings.length > 0 && (
        <section id={styles.container}>
          {myListings.map((ticket, index) => (
            <TicketCard
              key={index}
              ticket={ticket}
              setViewEvent={setViewEvent}
              setViewQR={setViewQR}
            />
          ))}
        </section>
      )}
    </section>
  );
}

const QRCodeDetails = ({ closeDetails, data }) => {
  const eventID = data.eventID;
  const qrData = data.ticket;
  const firstEightDigits = qrData.verifyToken.slice(0, 8);
  const remainingString = qrData.verifyToken.slice(9);
  const [loadingQRLogo, setLoadingQRLogo] = useState(true);

  const StatusContainer = () => {
    const [loading, setLoading] = useState(null);
    const [status, setStatus] = useState(qrData.verified);
    const intervalRef = useRef(null);

    useEffect(() => {
      if (loading !== null || status === true || status === 'refunded') return;

      async function getTicketStatus() {
        setLoading(true);
        try {
          const response = await axios.post(`${window.location.origin}/api/eventHub/tickets/getTicketStatus`, {
            eventID: eventID,
            verifyID: qrData.verifyToken,
            paymentIntentID: qrData.paymentIntentID,
          });
          setStatus(response.data);
        } catch (error) {
          errorMsg(error?.response?.data?.status || error.toString());
        } finally {
          setLoading(false);
        }
      }

      // Initial call to get ticket status
      getTicketStatus();

      // Set interval to fetch ticket status every 10 seconds if status is false
      intervalRef.current = setInterval(() => {
        if (status === false || status === null) {
          getTicketStatus();
        }
      }, 10000);

      // Cleanup function to clear interval
      return () => {
        clearInterval(intervalRef.current);
      };
    }, [status, eventID, qrData.verifyToken, qrData.paymentIntentID]);

    function refresh() {
      setStatus(null);
      setLoading(null);
      if(intervalRef.current) {
        clearInterval(intervalRef.current);
      }

    }

    return (
      <span id={styles.status}>
        {status === true && <FontAwesomeIcon icon={faCheckCircle} id={styles.refresh} />}
        {status !== true && <FontAwesomeIcon icon={faRotate} id={styles.refresh} onClick={refresh} />}
        {loading ? <BarLoader color="white" /> : <h1>{status === true ? "Verified" : "Not Verified"}</h1>}
      </span>
    );
  };

  function isLoading() {
    return loadingQRLogo;
  }

  const memoizedStatusState = useMemo(() => <StatusContainer />, []);

  return (
    <div id={styles.qrDetails}>
      <div>
        {isLoading() && (
          <span
            style={{
              backgroundColor: '#FFB81C',
              position: 'absolute',
              zIndex: 4,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <BarLoader color="black" />
          </span>
        )}

        <FontAwesomeIcon icon={faXmark} id={styles.exit} onClick={closeDetails} />

        {memoizedStatusState}

        <QRCode
          logoOnLoad={() => setLoadingQRLogo(false)}
          value={qrData.verifyToken}
          logoImage="/financehub/exchange-icon.png"
          logoWidth={65}
          logoHeight={65}
          bgColor="transparent"
          qrStyle="dots"
          fgColor="#154734"
          eyeRadius={10}
          eyeColor="#154734"
          size={300}
        />

        <span id={styles.code}>
          <h1>{firstEightDigits}</h1>
          <h2>-</h2>
          <h3>{remainingString}</h3>
        </span>
      </div>
    </div>
  );
};

const TicketCard = ({ ticket, setViewEvent, setViewQR }) => {
  const listingID = ticket.listingID;
  const listingName = ticket.listingName;
  const ticketData = ticket.ticketData;

  return (
    <div className={styles.card}>
      <h6>Total: ${(parseInt(ticketData.amount) / 100).toLocaleString()}</h6>

      <section>
        <FontAwesomeIcon icon={faTicket} id={styles.icon} />
      </section>

      <section>
        <h1>Event: {listingName}</h1>
        <h2>
          <b>Purchased:</b> {new Date(Date.parse(ticketData.creationDate)).toLocaleString()}
        </h2>
        <div>
          <button onClick={() => setViewQR({ ticket: ticketData, eventID: listingID })}>
            <FontAwesomeIcon icon={faQrcode} id={styles.icon} /> Ticket QR
          </button>
          <button onClick={() => setViewEvent(listingID)}>Event</button>
        </div>
      </section>
    </div>
  );
};
